import { pick } from "lodash-es";
import type { ModalProps } from "~/components/modal.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import CloseIcon from "~/components/icons/cross.tsx";
import Button from "~/components/button.tsx";
import PaletteIcon from "./palette-icon.tsx";
import backgroundImageLarge from "./background-lg.png?w=2732&h=2048&format=webp&quality=93&as=metadata";
import backgroundImageMedium from "./background-md.png?w=1366&h=1024&format=webp&quality=87&as=metadata";
import backgroundImageMobile from "./background-mobile.png?w=780&h=1688&format=webp&quality=87&as=metadata";
import ComparisonImage from "./comparison-image.tsx";
import classes from "./add-colour-modal.module.css";

type AddColourModalProps = Pick<ModalProps, "id"> & {
	readonly onConfirm: () => void;
};

function AddColourModal({ id, onConfirm }: AddColourModalProps) {
	const t = useTranslator();
	return (
		<dialog id={id} className={classes["add-colour-modal"]}>
			<picture>
				<source
					srcSet={backgroundImageLarge.src}
					media={`(min-width: ${backgroundImageMedium.width}px)`}
				/>
				<source
					srcSet={backgroundImageMedium.src}
					media={`(min-width: ${backgroundImageMobile.width}px)`}
				/>
				<img
					{...pick(backgroundImageMobile, "src", "width", "height")}
					alt={t("Colorful lego bricks")}
					className={classes["background"]}
				/>
			</picture>
			<button
				className={classes["close-button"]}
				aria-label={t("Close")}
				type="button"
				commandfor={id}
				command="close"
			>
				<CloseIcon />
			</button>
			<div className={classes["container"]}>
				<div className={classes["content"]}>
					<PaletteIcon />
					<h1 className="heading-1">
						{t("Enhance your creation with colors")}
					</h1>
					<div className={classes["summary"]}>
						{t(
							`You can now add color to your brick mosaic by ordering additional colored bricks! The next step will take you back to the design with extra color editing options. The additional cost will be calculated and shown before you proceed with the order.`,
						)}
					</div>
					<div className={classes["colourised-image"]}>
						<ComparisonImage />
					</div>
					<div className={classes["buttons"]}>
						<button
							type="button"
							onClick={onConfirm}
							className={classes["confirm-button"]}
							aria-label={t("Let's add colors")}
						>
							<div className={classes["confirm-button-text"]}>
								{t("Let's add colors")}
							</div>
						</button>
						<Button
							type="button"
							variant="text"
							commandfor={id}
							command="close"
						>
							{t("Nah, I prefer black & white.")}
						</Button>
					</div>
				</div>
			</div>
		</dialog>
	);
}

export default AddColourModal;
